import React from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';

/**
 * Dashboard filter bar (Phase 5A + Phase 12).
 *
 * `mode`:
 *   - 'full'  (default): period pills + 6 dropdowns (super_admin / manager).
 *   - 'agent': period pills + Product dropdown only (cc_agent Mon Dashboard).
 *
 * State shape (owned by the parent DashboardPage):
 *   {
 *     period: 'today' | 'yesterday' | 'this_week' | 'this_month' | 'custom',
 *     date_from?: ISO string (when period === 'custom'),
 *     date_to?:   ISO string (when period === 'custom'),
 *     call_center?, product?, city?, source?, shipping_company?
 *   }
 *
 * `filterValues` is the payload of GET /api/orders/filters (reused as-is).
 */
const PERIOD_PILLS = [
    { key: 'today', label: "Aujourd'hui" },
    { key: 'yesterday', label: 'Hier' },
    { key: 'this_week', label: 'Cette semaine' },
    { key: 'this_month', label: 'Ce mois' },
    { key: 'custom', label: 'Personnalisé' },
];

function fmtDay(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return iso; }
}

function toIsoStart(date) {
    if (!date) return undefined;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
}

function toIsoEnd(date) {
    if (!date) return undefined;
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
}

export default function DashboardFilters({ filters, onChange, filterValues, mode = 'full' }) {
    const setPeriod = (period) => {
        if (period === 'custom') {
            onChange({ ...filters, period: 'custom' });
        } else {
            const next = { ...filters, period };
            delete next.date_from;
            delete next.date_to;
            onChange(next);
        }
    };

    const setField = (key, value) => {
        const next = { ...filters };
        if (!value) delete next[key];
        else next[key] = value;
        onChange(next);
    };

    const isCustom = filters.period === 'custom';
    const [rangeOpen, setRangeOpen] = React.useState(false);
    const [tempRange, setTempRange] = React.useState({
        from: filters.date_from ? new Date(filters.date_from) : undefined,
        to: filters.date_to ? new Date(filters.date_to) : undefined,
    });

    React.useEffect(() => {
        setTempRange({
            from: filters.date_from ? new Date(filters.date_from) : undefined,
            to: filters.date_to ? new Date(filters.date_to) : undefined,
        });
    }, [filters.date_from, filters.date_to]);

    const applyRange = () => {
        if (tempRange.from && tempRange.to) {
            onChange({
                ...filters,
                period: 'custom',
                date_from: toIsoStart(tempRange.from),
                date_to: toIsoEnd(tempRange.to),
            });
            setRangeOpen(false);
        }
    };

    const cities = filterValues?.cities || [];
    const products = filterValues?.products || [];
    const shipping = filterValues?.shipping || [];
    const callCenters = filterValues?.call_centers || [];

    return (
        <div data-testid="dashboard-filters" className="bg-white rounded-xl border border-[#E5E7EB] p-4 shadow-[0_1px_2px_rgba(16,24,40,0.05)] space-y-3">
            {/* Row 1: period pills + custom-range popover */}
            <div className="flex items-center gap-2 flex-wrap">
                {PERIOD_PILLS.map((p) => {
                    const active = filters.period === p.key;
                    return (
                        <button
                            key={p.key}
                            type="button"
                            data-testid={`filter-period-${p.key}`}
                            onClick={() => setPeriod(p.key)}
                            className={
                                'h-9 px-4 rounded-lg text-sm font-medium transition-colors border ' +
                                (active
                                    ? 'bg-[#4B3FD8] text-white border-[#4B3FD8]'
                                    : 'bg-white text-[#1A1D2E] border-[#E5E7EB] hover:bg-[#F5F6FA]')
                            }
                        >
                            {p.label}
                        </button>
                    );
                })}

                {isCustom && (
                    <Popover open={rangeOpen} onOpenChange={setRangeOpen}>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                data-testid="filter-period-range-trigger"
                                className="h-9 px-3 rounded-lg text-sm font-medium border border-[#E5E7EB] bg-white hover:bg-[#F5F6FA] text-[#1A1D2E] inline-flex items-center gap-2"
                            >
                                <CalendarIcon size={16} strokeWidth={1.75} className="text-[#6B7280]" />
                                {filters.date_from && filters.date_to
                                    ? `${fmtDay(filters.date_from)} → ${fmtDay(filters.date_to)}`
                                    : 'Choisir une plage'}
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-white" align="start">
                            <Calendar
                                mode="range"
                                selected={tempRange}
                                onSelect={(r) => setTempRange(r || {})}
                                numberOfMonths={2}
                                data-testid="filter-period-range-calendar"
                            />
                            <div className="flex justify-end gap-2 p-3 border-t border-[#E5E7EB]">
                                <button
                                    type="button"
                                    onClick={() => setRangeOpen(false)}
                                    className="h-9 px-3 rounded-lg text-sm border border-[#E5E7EB] bg-white text-[#1A1D2E] hover:bg-[#F5F6FA]"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="button"
                                    data-testid="filter-period-range-apply"
                                    onClick={applyRange}
                                    disabled={!tempRange.from || !tempRange.to}
                                    className="h-9 px-4 rounded-lg text-sm bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white disabled:opacity-40"
                                >
                                    Appliquer
                                </button>
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
            </div>

            {/* Row 2: dropdowns. Full mode → 5 dropdowns (SA/manager).
                Agent mode → only the Product dropdown (cc_agent Mon Dashboard).
                Phase 16.2 — the RÉGION dropdown was retired from this bar
                (the field stays in DB / side panel / Shopify webhook mapping). */}
            {mode === 'full' && (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
                <Select label="Call Center" value={filters.call_center} onChange={(v) => setField('call_center', v)} options={callCenters.map((c) => ({ value: c.id, label: c.commercial_name || c.name }))} testId="filter-call-center" />
                <Select label="Produit" value={filters.product} onChange={(v) => setField('product', v)} options={products.map((p) => ({ value: p, label: p }))} testId="filter-product" />
                <Select label="Ville" value={filters.city} onChange={(v) => setField('city', v)} options={cities.map((c) => ({ value: c, label: c }))} testId="filter-city" />
                <Select label="Source" value={filters.source} onChange={(v) => setField('source', v)} options={[{ value: 'website', label: 'Website' }, { value: 'whatsapp', label: 'WhatsApp' }]} testId="filter-source" />
                <Select label="Transporteur" value={filters.shipping_company} onChange={(v) => setField('shipping_company', v)} options={shipping.map((s) => ({ value: s, label: s }))} testId="filter-shipping" optionsUppercase />
            </div>
            )}
            {mode === 'agent' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                <Select label="Produit" value={filters.product} onChange={(v) => setField('product', v)} options={products.map((p) => ({ value: p, label: p }))} testId="filter-product" />
            </div>
            )}
        </div>
    );
}

function Select({ label, value, onChange, options, testId, optionsUppercase = false }) {
    // Same uppercase pattern as OrdersFilters.jsx NativeSelect (Phase 14.8):
    //   - real <option>s carry `uppercase` → open dropdown shows carriers in
    //     majuscules;
    //   - the "Tous" placeholder gets an inline `text-transform: none` to
    //     survive inheritance when the parent <select> is uppercased;
    //   - the parent <select> is uppercased only when a real value is picked
    //     (Chromium doesn't propagate per-<option> text-transform to the
    //     closed trigger).
    const triggerUppercase = optionsUppercase && !!value;
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">{label}</span>
            <select
                data-testid={testId}
                value={value || ''}
                onChange={(e) => onChange(e.target.value || undefined)}
                className={`h-9 px-2 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25 ${triggerUppercase ? 'uppercase' : ''}`}
            >
                <option
                    value=""
                    style={optionsUppercase ? { textTransform: 'none' } : undefined}
                >
                    Tous
                </option>
                {options.map((o) => (
                    <option
                        key={o.value}
                        value={o.value}
                        className={optionsUppercase ? 'uppercase' : ''}
                    >
                        {o.label}
                    </option>
                ))}
            </select>
        </label>
    );
}
